import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { NavigateOptions, useNavigate } from 'react-router-dom'

import { COMMON_MIXPANEL_PROPERTIES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { ESSENTIALS_ORIENTATION_MIXES_MODAL } from '@lyrahealth-inc/shared-app-logic/src/features/essentials/constants'
import {
  ESSENTIALS_CONTENT_CATEGORY,
  ESSENTIALS_SITE_CATEGORY,
  useNavigateToEssentialsContentProps,
} from '@lyrahealth-inc/shared-app-logic/src/features/essentials/types'
import { getEssentialsSubpath } from '@lyrahealth-inc/shared-app-logic/src/features/essentials/utils'
import { openUrl } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/expo-linking'

import { useDefaultMixpanelPage } from './useDefaultMixpanelPage'
import { getIsUserLoggedIn } from '../../data/user/userSelectors'

export interface useNavigateToEssentialsContentWebProps extends useNavigateToEssentialsContentProps {
  /** Overrides back/close button behavior to take the user back to the lyra app */
  parentAppReferrer?: string
  newWindow?: boolean
  navigateOptions?: NavigateOptions
  loggedIn?: boolean
}

/**
 * Navigate to essentials content
 * - Omit slug and subPath to go to the essentials homepage
 */
export const useNavigateToEssentialsContent = () => {
  const navigate = useNavigate()
  const defaultMixpanelPage = useDefaultMixpanelPage()
  const isLoggedIn = useSelector(getIsUserLoggedIn)

  return useCallback(
    ({
      slug,
      entryPoint,
      category = ESSENTIALS_CONTENT_CATEGORY.ARTICLE,
      newWindow = false,
      subPath,
      parentAppReferrer,
      activeTabOverride,
      locale,
      navigateOptions,
      referrer,
      toggleTranscript,
      mixpanelEventsAddon,
      site = ESSENTIALS_SITE_CATEGORY.ESSENTIALS,
      referrerPage,
      loggedIn,
      shouldOpenOrientationMixes,
    }: useNavigateToEssentialsContentWebProps) => {
      const mixpanelEntryPoint = entryPoint || defaultMixpanelPage.page
      const subPathStr = getEssentialsSubpath({ slug, category, subPath })
      const options = navigateOptions ?? {}
      if (referrerPage) {
        options.state = {
          ...options.state,
          [COMMON_MIXPANEL_PROPERTIES.REFERRER_PAGE]: referrerPage,
        }
      }

      const baseRoute = loggedIn ?? isLoggedIn ? `/secure/index/` : '/'

      const url = `${baseRoute}${site}${subPathStr}?${new URLSearchParams({
        ...(mixpanelEntryPoint && { [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: mixpanelEntryPoint }),
        ...(parentAppReferrer && { parentAppReferrer }),
        ...(activeTabOverride && { activeTabOverride }),
        ...(locale && { locale }),
        ...(referrer && { referrer }),
        ...(toggleTranscript && { toggleTranscript }),
        ...(mixpanelEventsAddon && { mixpanelEventsAddon: JSON.stringify(mixpanelEventsAddon) }),
        ...(shouldOpenOrientationMixes && { [ESSENTIALS_ORIENTATION_MIXES_MODAL]: 'true' }),
      })}`
      if (newWindow) {
        openUrl(`${window.location.origin}${url}`)
      } else {
        navigate(url, options)
      }
    },
    [defaultMixpanelPage.page, isLoggedIn, navigate],
  )
}
